import React from 'react';
import Button from 'ui/elements/buttons/Button';
import ButtonList from 'ui/elements/buttons/ButtonList';
import LocationInput from 'domain/shared/Location/LocationInput';
import { fontSizeH3 } from 'ui/theme/themeBluePlanet';
import { bluePlanetTheme } from 'ui/theme';
import { Content, DialogActions, Title } from 'ui/views/dialogs/Dialog';
import { Location } from 'apis/MapboxAPI';
import FormikHelperText from 'ui/elements/form/formik/FormikTextHelperText';

import { useCompanyMetadataForm } from 'hooks/onboarding';
import NumberInput from 'ui/elements/form/numbers/NumberInput';

interface CompanyMetadataInputProps {
  onSubmit: (data: { location: Location; founded: string }) => void;
  onUseManualInput: () => void;
}

export default function CompanyMetadataInput({ onSubmit, onUseManualInput }: CompanyMetadataInputProps) {
  const formik = useCompanyMetadataForm({ onSubmit });

  return (
    <>
      <Title className="text-h3">Tell us about your company to get started</Title>
      <Content>
        <p className="text-metadata">
          We&apos;ll use this information to create an initial draft of your company profile using our AI helper. You
          can change and edit everything at any time.
        </p>
        <div className="u-content-spacing-top text-xx-large">
          We were founded in&nbsp;
          <NumberInput
            autoFocus
            min={1980}
            max={new Date().getFullYear()}
            name="founded"
            value={formik.values.founded}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          , and are located in{' '}
          <LocationInput
            searchArea="places"
            name="location"
            onChange={v => {
              formik.setFieldValue('location', v);
            }}
            sx={{ maxWidth: '400px' }}
            textFieldSx={{
              '.MuiInput-root': {
                border: 'none',
                borderBottom: `1px solid ${bluePlanetTheme.bluePlanetPalette.indigo.main}`,
                borderRadius: 0,
              },
              '.MuiInputBase-input': {
                fontSize: fontSizeH3,
              },
            }}
            value={formik.values.location}
          />
          {formik.errors.founded && <FormikHelperText name="founded" {...formik} />}
          {!formik.errors.founded && <FormikHelperText name="location" {...formik} />}
        </div>
      </Content>
      <DialogActions>
        <ButtonList>
          <Button
            kind="primary"
            onClick={() => {
              formik.submitForm();
            }}
          >
            Continue with AI
          </Button>
          <Button kind="tertiary" onClick={onUseManualInput}>
            Don&apos;t use AI
          </Button>
        </ButtonList>
      </DialogActions>
    </>
  );
}
